const ABBREVIATIONS = new Set([
  "e.g.",
  "i.e.",
  "mr.",
  "mrs.",
  "ms.",
  "dr.",
  "prof.",
  "sr.",
  "jr.",
  "vs.",
  "etc.",
  "u.s.",
  "u.k.",
  // German; the worker's sentence splitter (denker-agent-voice-chunks.mjs)
  // keeps the same list.
  "ca.",
  "bzw.",
  "ggf.",
  "usw.",
  "vgl.",
  "inkl.",
  "evtl.",
  "bspw.",
  "sog.",
  "nr.",
  "mio.",
  "mrd.",
  "std.",
  "min.",
]);

// Sentence ends that need whitespace after them (a dot inside example.com is
// no pause), and the CJK and Devanagari ones that never have any.
const SPACED_TERMINATORS = new Set([".", "!", "?"]);
const UNSPACED_TERMINATORS = new Set(["。", "！", "？", "।"]);
const SENTENCE_CLOSERS = /["'”’)\]}*_`」』）》]/;

/** Text that ends in a sentence terminator, closers and spaces aside. */
export const ENDS_WITH_TERMINATOR = new RegExp(
  `[${[...SPACED_TERMINATORS, ...UNSPACED_TERMINATORS].join("")}]${SENTENCE_CLOSERS.source}*\\s*$`,
);

const STREAMING_GROUP_SENTENCE_LIMIT = 2;
const STREAMING_GROUP_TARGET_CHARS = 200;

export type SentenceSegment = {
  text: string;
  consumed: number;
};

export function splitSpeechSegments(
  text: string,
  includeTail: boolean,
): SentenceSegment[] {
  const segments: SentenceSegment[] = [];
  let segmentStart = 0;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i] ?? "";
    const unspaced = UNSPACED_TERMINATORS.has(char);
    if (!unspaced && !SPACED_TERMINATORS.has(char)) continue;
    if (insideMarkdownLink(text, i)) continue;
    if (char === "." && isProtectedPeriod(text, i, segmentStart)) continue;
    const consumed = consumeTrailingSentenceClosers(
      text,
      unspaced ? consumeTerminatorRun(text, i + 1) : i + 1,
    );
    // Punctuation inside URLs, filenames and email addresses is not a pause.
    // At the end of a token chunk a period remains ambiguous until more text
    // arrives (or the final response flushes it with includeTail).
    if (
      !unspaced &&
      consumed < text.length &&
      !/\s/.test(text[consumed] ?? "")
    ) {
      continue;
    }
    if (!includeTail && consumed === text.length && char === ".") continue;
    const segment = text.slice(segmentStart, consumed).trim();
    // A mark that arrived after its sentence was spoken ("好！" then "？")
    // opens the next sentence; it is never a sentence of its own.
    if (!hasSpeech(segment)) continue;
    segments.push({ text: segment, consumed });
    segmentStart = consumed;
    i = consumed - 1;
  }

  if (includeTail) {
    const tail = text.slice(segmentStart).trim();
    if (tail) segments.push({ text: tail, consumed: text.length });
  }
  return segments;
}

/** Text with something to say: a letter or a digit, not marks alone. */
export function hasSpeech(text: string): boolean {
  return /[\p{L}\p{N}]/u.test(text);
}

export function firstSentences(text: string, count: number): string {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const segments = splitSpeechSegments(trimmed, true);
  if (segments.length === 0) return trimmed;
  return segments
    .slice(0, count)
    .map((segment) => segment.text)
    .join(" ")
    .trim();
}

// A stop inside a link's label or destination is no pause. Only a `[` that
// opens a real link counts: one followed by `](`, or one whose label is still
// arriving. A pending label that already spans another sentence break is
// plain bracketed text, so the stream keeps moving.
function insideMarkdownLink(text: string, index: number): boolean {
  const open = text.lastIndexOf("[", index);
  if (open < 0) return false;
  const labelEnd = text.indexOf("]", open);
  if (labelEnd < 0) return !/[.!?]\s+\S/.test(text.slice(index + 1));
  if (labelEnd === text.length - 1) return labelEnd > index;
  if (text[labelEnd + 1] !== "(") return false;
  if (labelEnd > index) return true;
  const destinationEnd = text.indexOf(")", labelEnd + 2);
  return destinationEnd < 0 || destinationEnd >= index;
}

export function speechGroups(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  const sentences = splitSpeechSegments(trimmed, true).map(
    (segment) => segment.text,
  );
  if (sentences.length === 0) return [trimmed];

  const groups: string[] = [];
  let current: string[] = [];
  let currentLength = 0;
  for (const sentence of sentences) {
    const nextLength =
      currentLength + (current.length ? 1 : 0) + sentence.length;
    const shouldFlush =
      current.length > 0 &&
      (current.length >= STREAMING_GROUP_SENTENCE_LIMIT ||
        nextLength > STREAMING_GROUP_TARGET_CHARS);
    if (shouldFlush) {
      groups.push(current.join(" "));
      current = [];
      currentLength = 0;
    }
    current.push(sentence);
    currentLength += (currentLength ? 1 : 0) + sentence.length;
  }
  if (current.length) groups.push(current.join(" "));
  return groups;
}

/** A bare list number ("1.", "12."): a marker, never a sentence of its own. */
export function isListNumberToken(text: string): boolean {
  return /^\d{1,3}\.$/.test(text);
}

// The opening can be a clause: Kokoro synthesizes a whole group before its
// first word plays, so a short first clause is heard sooner than a sentence.
const OPENING_CLAUSE_MIN_CHARS = 25;
const CLAUSE_MARKS = new Set([",", ";", ":", "—", "–", "-"]);

/** The first clause of a stream's opening, once a later word confirms it. */
export function openingClause(text: string): SentenceSegment | null {
  const sentenceEnd = splitSpeechSegments(text, false)[0]?.consumed;
  const limit = Math.min(sentenceEnd ?? text.length, text.length - 1);
  for (let i = OPENING_CLAUSE_MIN_CHARS; i < limit; i += 1) {
    if (!CLAUSE_MARKS.has(text[i] ?? "") || !/\s/.test(text[i + 1] ?? ""))
      continue;
    if (text[i] === "-" && !/\s/.test(text[i - 1] ?? "")) continue;
    if (insideMarkdownLink(text, i) || !/\S/.test(text.slice(i + 1))) continue;
    const clause = text.slice(0, i + 1).trim();
    if (hasSpeech(clause)) return { text: clause, consumed: i + 1 };
  }
  return null;
}

// A lead-in ("Let me check:") ends a turn's commentary before a tool runs.
// A time ("10:") or a URL scheme ("https:") is still arriving, not a lead-in.
export const ENDS_WITH_LEAD_IN = new RegExp(
  `(?:(?<!\\d|\\bhttps?):|[—–])${SENTENCE_CLOSERS.source}*\\s*$`,
);

export function completedStreamingGroup(text: string): SentenceSegment | null {
  const segments = splitSpeechSegments(text, false);
  const first = segments[0];
  if (!first || isListNumberToken(first.text)) return null;

  // Start with a genuine completed sentence; partial text and list markers
  // must not trigger speech before their content arrives.
  return first;
}

// "！？" or "。。。" ends one sentence, not one per mark.
function consumeTerminatorRun(text: string, index: number): number {
  let end = index;
  while (
    end < text.length &&
    (UNSPACED_TERMINATORS.has(text[end] ?? "") ||
      SPACED_TERMINATORS.has(text[end] ?? ""))
  ) {
    end += 1;
  }
  return end;
}

function consumeTrailingSentenceClosers(text: string, index: number): number {
  let end = index;
  while (end < text.length && SENTENCE_CLOSERS.test(text[end] ?? "")) end += 1;
  return end;
}

function isProtectedPeriod(
  text: string,
  index: number,
  segmentStart: number,
): boolean {
  const previous = text[index - 1] ?? "";
  const next = text[index + 1] ?? "";
  if (/\d/.test(previous) && /\d/.test(next)) return true;
  if (isNumberedListMarker(text, index, segmentStart)) return true;
  // Only a dot followed by another dot is protected (ellipsis runs, "..").
  // The last dot of a run must remain a boundary, otherwise "Just a
  // moment..." never completes a streaming sentence. Dots inside domains
  // (example.com) are guarded separately by the whitespace rule below.
  if (next === ".") return true;

  let start = index;
  while (start > 0 && /[\p{L}\p{M}.]/u.test(text[start - 1] ?? "")) start -= 1;
  let end = index + 1;
  while (end < text.length && /[\p{L}\p{M}.]/u.test(text[end] ?? "")) end += 1;
  const token = text.slice(start, end).toLowerCase();
  if (ABBREVIATIONS.has(token)) return true;

  const letters = token.replace(/\./g, "");
  if (/^(?:[a-z]\.){2,}$/.test(token)) return true;
  if (letters.length === 1 && (!next || /\s|[,;:)\]}]/.test(next))) {
    return true;
  }
  return false;
}

function isNumberedListMarker(
  text: string,
  index: number,
  segmentStart: number,
): boolean {
  const next = text[index + 1] ?? "";
  if (!/\s/.test(next)) return false;

  let markerStart = index;
  while (markerStart > segmentStart && /\d/.test(text[markerStart - 1] ?? "")) {
    markerStart -= 1;
  }
  const previous = text[markerStart - 1] ?? "";
  if (markerStart > segmentStart && !/\s/.test(previous)) return false;

  const marker = text.slice(markerStart, index);
  if (!/^\d{1,3}$/.test(marker)) return false;

  return /\S/.test(text.slice(index + 1));
}
